import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Eye, Star } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../AdminLayout";
import { adminAxios, formatErr } from "../api";
import { useCatalog } from "../../contexts/CatalogContext";

const STATUS_MAP = {
  active: { bg: "#DCFCE7", color: "#22C55E", label: "Active" },
  deactivated: { bg: "#F1F5F9", color: "#64748B", label: "Deactivated" },
  pending: { bg: "#FEF3C7", color: "#F59E0B", label: "Pending" },
};

const Pill = ({ status }) => {
  const m = STATUS_MAP[status] || STATUS_MAP.pending;
  return <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ backgroundColor: m.bg, color: m.color }}>{m.label}</span>;
};

export default function AdminTechnicians() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const { categories: CATEGORIES } = useCatalog();

  const queryParams = useMemo(() => ({ page, pageSize }), [page, pageSize]);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set("limit", String(pageSize));
      qs.set("skip", String((page - 1) * pageSize));
      const { data: d } = await adminAxios.get(`/technicians?${qs.toString()}`);
      setData({ total: d.total || 0, items: d.items || [] });
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [queryParams]);

  return (
    <AdminLayout title="Technicians">
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 text-sm text-slate-500 flex items-center justify-between">
          <span>{loading ? "Loading…" : `Showing ${Math.min((page - 1) * pageSize + 1, data.total)} - ${Math.min(page * pageSize, data.total)} of ${data.total} technicians`}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="technicians-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="px-5 py-2.5">Name</th>
                <th className="px-5 py-2.5">Phone</th>
                <th className="px-5 py-2.5">City</th>
                <th className="px-5 py-2.5">Skills</th>
                <th className="px-5 py-2.5">Rating</th>
                <th className="px-5 py-2.5">Jobs</th>
                <th className="px-5 py-2.5">Status</th>
                <th className="px-5 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-400">
                  No technicians yet. <Link to="/admin/applications?status=pending" className="text-[#1A3C6E] font-semibold underline">Review applications</Link>.
                </td></tr>
              )}
              {data.items.map((t) => (
                <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`tech-row-${t.id}`}>
                  <td className="px-5 py-3">
                    <Link to={`/admin/technicians/${t.id}`} className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#1A3C6E] text-white flex items-center justify-center font-semibold text-xs">
                        {t.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <span className="font-semibold text-slate-900 hover:underline">{t.name}</span>
                    </Link>
                  </td>
                  <td className="px-5 py-3">{t.phone}</td>
                  <td className="px-5 py-3 capitalize">{t.city}</td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(t.services || []).map((sid) => {
                        const c = CATEGORIES.find((c) => c.id === sid);
                        return <span key={sid} className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: c?.bg || "#E2E8F0", color: c?.color || "#475569" }}>{c?.short || sid}</span>;
                      })}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-1 text-slate-700">
                      <Star className="w-3.5 h-3.5 text-[#FF6B2B] fill-[#FF6B2B]" /> {(t.rating || 0).toFixed(1)}
                    </span>
                  </td>
                  <td className="px-5 py-3">{t.jobs_completed || 0}</td>
                  <td className="px-5 py-3"><Pill status={t.status} /></td>
                  <td className="px-5 py-3 text-right">
                    <Link to={`/admin/technicians/${t.id}`} className="p-1.5 rounded-md hover:bg-slate-200 inline-block" title="View" data-testid={`tech-view-${t.id}`}>
                      <Eye className="w-4 h-4 text-slate-600" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-3 py-1 rounded-md border">Previous</button>
          <button disabled={page * pageSize >= data.total} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 rounded-md border">Next</button>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-500">Per page</label>
          <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="px-2 py-1 border rounded">
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>
    </AdminLayout>
  );
}
